import AuthenticationApi from '@Features/Authentication/AuthenticationApi/Authentication.api'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import SettingsApi from '../SettingsApi/Settings.api'
import { SettingsSliceInitialState } from './Settings.slice.types'

const SLICE_BASE_NAME = 'settings'

export const getSettings = createAsyncThunk(`${SLICE_BASE_NAME}/all`, async () => {
	const { data } = await SettingsApi.getInstance().getSettings()
	return data
})

export const getAssets = createAsyncThunk(`${SLICE_BASE_NAME}/all/assets`, async () => {
	const { data } = await SettingsApi.getInstance().getAssets()
	return data
})
export const getEducationalInfo = createAsyncThunk(`${SLICE_BASE_NAME}/all-edu-info`, async () => {
	const { data } = await AuthenticationApi.getEducationalInfo()
	return data
})
const initialState: SettingsSliceInitialState = {
	assetsLoading: false,
	classroomsSections: {
		exams_section: false,
		online_cl: false,
		revision_section: false,
		device_limit: 0,
	},
	paymentMethods: {
		recharge_card: false,
		aman_accept: false,
		credit_card_accept: false,
		ewallet_accept: false,
		ewallet_opay: false,
		fawry_fawry: false,
		masary_accept: false,
		credit_card_opay: false,
		ewallet_fawry: false,
		fawry_moneyhash: false,
		credit_card_moneyhash: false,
		ewallet_moneyhash: false,
		credit_card_kashier: false,
		ewallet_kashier: false,
		manual_vc_enabled: false,
	},
	fees: {},
	parent_portal: false,
	progress: false,
	classrooms_enabled: true,
	standalone_courses_enabled: false,
	bundles_enabled: false,
	unit_notes_enabled: true,
	unit_qa_enabled: true,
	created_at: undefined,
	updated_at: undefined,
	isOnline: true,
	theme: 'primary',
	assets: {},
}

const settingsSlice = createSlice({
	name: SLICE_BASE_NAME,
	initialState,
	reducers: {
		updateOnlineStatus: (state, { payload }: PayloadAction<boolean>) => {
			state.isOnline = payload
		},
	},
	extraReducers: builder => {
		builder
			.addCase(getSettings.pending, () => {})
			.addCase(getSettings.fulfilled, (state, { payload: { data } }) => {
				state.paymentMethods = {
					aman_accept: data.aman_accept,
					credit_card_accept: data.credit_card_accept,
					ewallet_accept: data.ewallet_accept,
					ewallet_opay: data.ewallet_opay,
					fawry_fawry: data.fawry_fawry,
					masary_accept: data.masary_accept,
					recharge_card: data.recharge_card,
					ewallet_fawry: data.ewallet_fawry,
					credit_card_opay: data.credit_card_opay,
					fawry_moneyhash: data.fawry_moneyhash,
					credit_card_moneyhash: data.credit_card_moneyhash,
					ewallet_moneyhash: data.ewallet_moneyhash,
					credit_card_kashier: data.credit_card_kashier,
					ewallet_kashier: data.ewallet_kashier,
					manual_vc_enabled: data.manual_vc_enabled ?? false,
				}

				state.classroomsSections = {
					exams_section: data.exams_section,
					online_cl: data.online_cl,
					revision_section: data.revision_section,
					device_limit: data.device_limit,
				}
				state.fees = {
					amount: data.registration_fees,
					old_amount: data.surcharge_registration_fees,
				}
				state.parent_portal = data.parent_portal
				state.progress = data.progress
				state.classrooms_enabled = data.classrooms_enabled ?? true
				state.standalone_courses_enabled = data.standalone_courses_enabled ?? false
				state.bundles_enabled = data.bundles_enabled ?? false
				// `?? true` keeps a backend that predates the columns on today's
				// behaviour: both unit tabs visible.
				state.unit_notes_enabled = data.unit_notes_enabled ?? true
				state.unit_qa_enabled = data.unit_qa_enabled ?? true
				state.created_at = data.created_at
				state.updated_at = data.updated_at
				state.theme = data.theme
				state.landing_headline = data.landing_headline
				state.landing_subheadline = data.landing_subheadline
				state.about_us_page = data.about_us_page
				state.fb_link = data.fb_link
				state.youtube_link = data.youtube_link
				state.instructor_section = data.instructor_section
				state.subjects_section = data.subjects_section
				state.instagram_link = data.instagram_link
				state.linkedin_link = data.linkedin_link
				state.privacy_link = data.privacy_link
				state.terms_link = data.terms_link
				state.meta = {
					title: data.meta_title,
				}
				state.app_wallet = data.app_wallet
				state.marketing = {
					gtm_container_id: data.gtm_container_id ?? null,
					ga4_measurement_id: data.ga4_measurement_id ?? null,
					meta_pixel_id: data.meta_pixel_id ?? null,
					tiktok_pixel_id: data.tiktok_pixel_id ?? null,
					clarity_project_id: data.clarity_project_id ?? null,
				}
			})
		builder.addCase(getEducationalInfo.fulfilled, (state, { payload }) => {
			state.educationalInfo = payload.data
		})
		builder
			.addCase(getAssets.pending, state => {
				state.assetsLoading = true
			})
			.addCase(getAssets.fulfilled, (state, { payload }) => {
				state.assets = payload.data
				state.assetsLoading = false
			})
	},
})

export const { updateOnlineStatus } = settingsSlice.actions
export default settingsSlice
